import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS} from '@contentful/rich-text-types'
import type {StackSpacingVariants} from '@primer/react-brand'
import {Box, Stack} from '@primer/react-brand'
import {createContext, useContext} from 'react'
import {useCampaign} from '../../../lib/hooks/use-campaign'

import type {Form as ContentfulFormEntry} from '../../../schemas/contentful/contentTypes/form'
import {getAnalyticsEvent} from '../../../lib/utils/analytics'

import {Form, type FormHeadingProps} from '../../forms/Form/Form'
import {FormContext} from '../../forms/Form/FormContext'
import {useForm, type OnSubmit} from '../../forms/Form/hooks/useForm'

import {ContentfulFormTwoColumnLayout} from './ContentfulFormTwoColumnLayout'
import {ContentfulFormSingleColumnLayout} from './ContentfulFormSingleColumnLayout'

export const ContentfulFormContext = createContext<{marketingFormsApiHost?: string}>({})

export type ContentfulFormProps = {
  component: ContentfulFormEntry

  /**
   * Use this prop to customize how the form heading is rendered.
   */
  headingProps?: Pick<FormHeadingProps, 'size' | 'as'>

  /**
   *
   * Use this prop to customize the padding of the form.
   */
  padding?: (typeof StackSpacingVariants)[number]

  /**
   * Use this prop to execute some code after the form has completed submission.
   */
  onSubmitted?: () => unknown

  /**
   * Use this prop to take control of the form submission behavior. This likely won’t be used in
   * production code, but they are useful for testing purposes (e.g., Storybook or
   * component tests).
   *
   * If you simply want to be notified when the form is submitted, use the `onSubmitted` prop instead.
   */
  onSubmit?: OnSubmit

  /**
   * Use this prop to disable the consent experience. Please note this may lead to compliance issues
   * and non-valid submissions.
   */
  skipConsentExperience?: boolean

  /**
   * Use this prop to disable Octocaptcha. Please note this may lead to non-valid submissions.
   */
  skipOctocaptcha?: boolean
}

export function ContentfulForm({component, ...props}: ContentfulFormProps) {
  const ctx = useContext(ContentfulFormContext)
  const formContext = useForm()

  const {campaign} = component.fields
  const {isEloqua, isMarketo, redirectUrl, elqFormName, ...additionalData} = useCampaign(campaign)
  const {source} = additionalData

  // Don't render the form when campaign is undefined (required in Contentful) as we don't know where to send the submitted lead
  if (!campaign) {
    return null
  }

  const submitToMarketingFormsApi: OnSubmit = async values => {
    if (typeof ctx.marketingFormsApiHost !== 'string') {
      throw new Error("The 'marketingFormsApiHost' context value is missing.")
    }

    /**
     * This value is used by the Marketing Forms API service to identify the form submissions
     * and provide further observability into the form submission process. It has no impact
     * on the form submission itself.
     */
    const FORM_ID = `contentful-${component.sys.id}`

    /**
     * We need to separate the primary consent from the rest of the payload data because it isn't
     * required for the marketing forms api
     */
    const {primaryConsent, ...submittedValues} = values

    await fetch(`${ctx.marketingFormsApiHost}/forms/${FORM_ID}/submissions`, {
      method: 'POST',

      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },

      body: new URLSearchParams({
        ...submittedValues,
        ...additionalData,

        // This is a required field for the Marketing Forms API but we don't
        // use the redirection behavior here.
        redirect_url: window.location.href,
      }),

      /**
       * The Marketing Forms API always responds with a redirection. We use the
       * 'manual' strategy to ensure the browser does not follow the redirection
       * and we can handle the on submitted behavior.
       */
      redirect: 'manual',
    })
  }

  const handleFormSubmission: OnSubmit = async values => {
    if (props.onSubmit) {
      await props.onSubmit({...values, ...additionalData})
    } else if (isMarketo) {
      await submitToMarketingFormsApi(values)
    }

    if (props.onSubmitted) {
      await props.onSubmitted()
    }
  }

  const layout = component.fields.layoutOption
  const action = isEloqua ? {action: 'https://s88570519.t.eloqua.com/e/f2'} : {}
  const formProps = isEloqua ? action : {onSubmit: formContext.handleSubmit(handleFormSubmission)}

  return (
    <FormContext value={formContext}>
      <Form {...formProps}>
        <Stack direction="vertical" gap="condensed" padding="none">
          {component.fields.heading !== undefined
            ? documentToReactComponents(component.fields.heading, {
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (_, children) => <Form.Heading {...props.headingProps}>{children}</Form.Heading>,
                },
              })
            : null}

          {layout.sys.contentType.sys.id === 'formLayout' && (
            <ContentfulFormSingleColumnLayout
              formFields={'formFields' in layout.fields ? layout.fields.formFields : []}
              formContext={formContext}
            />
          )}

          {layout.sys.contentType.sys.id === 'twoColumnFormLayout' && (
            <ContentfulFormTwoColumnLayout
              rows={'rows' in layout.fields ? layout.fields.rows : []}
              formContext={formContext}
            />
          )}

          {!props.skipConsentExperience && (
            <>
              <Form.CountrySelectField />
              <Form.ConsentExperience />
            </>
          )}

          {!props.skipOctocaptcha && <Form.Octocaptcha />}

          {isEloqua && (
            <>
              {elqFormName ? <input type="hidden" name="elqFormName" value={elqFormName} /> : null}
              <input type="hidden" name="elqSiteId" value="88570519" />
              <input type="hidden" name="redirect_url" value={redirectUrl} />
              {source ? <input type="hidden" name="source" value={source} /> : null}
            </>
          )}

          <Form.Submit
            {...getAnalyticsEvent({
              action: component.fields.submitText,
              tag: 'button',
              context: 'form_submit',
              location: 'form',
            })}
          >
            {component.fields.submitText}
          </Form.Submit>

          <Box marginBlockStart={'normal'}>
            <Form.Errors />
          </Box>
        </Stack>
      </Form>
    </FormContext>
  )
}

try{ ContentfulFormContext.displayName ||= 'ContentfulFormContext' } catch {}
try{ ContentfulForm.displayName ||= 'ContentfulForm' } catch {}